import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { CommonPacks } from './CommonPacks/index.tsx';
import type {
  CommonPacksQuery,
  CommonPacksQueryVariables,
} from './__generated__/index.graphql.ts';

const COMMON_PACKS_QUERY = gql`
  query CommonPacksQuery($sport: Sport!) {
    market {
      id
      ...CommonPacks_marketRoot
    }
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        commonGameOnboarded
      }
    }
    tokens {
      ...CommonPacks_tokenRoot
    }
  }
  ${CommonPacks.fragments.marketRoot}
  ${CommonPacks.fragments.tokenRoot}
` as TypedDocumentNode<CommonPacksQuery, CommonPacksQueryVariables>;

export const CommonPacksPage = () => {
  const { sport } = useSportContext();
  const { data, refetch, loading } = useQuery(COMMON_PACKS_QUERY, {
    variables: {
      sport,
    },
  });

  const { refetch: refetchCurrentUser } = useCurrentUserContext();

  const { market, tokens } = data || {};

  const refetchAll = async () =>
    // also refetch current user to update the common card shards count, gems balance, etc.
    // (useful on ios when things are not always in sync)
    Promise.all([refetch(), refetchCurrentUser()]);

  if (loading || !data) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <CommonPacks marketRoot={market} tokenRoot={tokens} refetch={refetchAll} />
  );
};
